import React, { useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { selectActiveView, selectedRootOffice, setActiveView } from "../../../../store/officeView.duck";
import styled from "styled-components";
import {
  OfficeWhereaboutsView,
  selectActivePeriod,
  setActivePeriod,
} from "../../../../store/editMovements.duck";
import { Period } from '@features/whereabouts/models/movements.models';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { SingleOfficeUsageView } from "./SingleOfficeUsageView";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { AreaAndDeskUsageView } from "./AreaAndDeskUsageView";
import { OfficeCarParkingList } from "./OfficeCarParkingList";

enum OfficeConfigurationType {
  None, Office, Floors, AreasAndDesks
}

export function OfficeUsageView() {
  const dispatch = useDispatch();
  const activePeriod = useSelector(selectActivePeriod);
  const rootOffice = useSelector(selectedRootOffice);
  const allOffices = useSelector(selectAllOfficeEntities);
  const activeView = useSelector(selectActiveView);

  const officeConfigurationType = useMemo(() => {
    if (rootOffice && allOffices.length > 0) {
      const rootOfficeChildren = allOffices.filter((o: OfficeEntity) => o.parentId === rootOffice.id);
      if (rootOfficeChildren.length === 0) {
        return OfficeConfigurationType.Office;
        // Maybe add floors in
      } else {
        return OfficeConfigurationType.AreasAndDesks;
      }
    }
    return OfficeConfigurationType.None;
  }, [rootOffice, allOffices]);

  const onPeriodChange = (period: Period) => {
    dispatch(setActivePeriod(period))
    dispatch(setActiveView(OfficeWhereaboutsView.Office))
  }

  return (
    <OfficesUsageListViewContainer data-test="office-members-container">
      <ViewControls>
        <ToggleableOutlineButton fullWidth={true} text="button.all-day" dataTest={"all-day-button"}
                                 click={() => onPeriodChange(Period.AllDay)}
                                 isActive={activePeriod === Period.AllDay && activeView === OfficeWhereaboutsView.Office}/>
        <ToggleableOutlineButton fullWidth={true} text="button.am-only" dataTest={"am-day-button"}
                                 click={() => onPeriodChange(Period.AM)}
                                 isActive={activePeriod === Period.AM && activeView === OfficeWhereaboutsView.Office}/>
        <ToggleableOutlineButton fullWidth={true} text="button.pm-only" dataTest={"pm-day-button"}
                                 click={() => onPeriodChange(Period.PM)}
                                 isActive={activePeriod === Period.PM && activeView === OfficeWhereaboutsView.Office}/>

        {(!!rootOffice?.carParkCapacity || !!rootOffice?.hasCarParkPlan) && <>

          <ToggleableOutlineButton fullWidth={true} text="whereabouts.parking"
                                   click={() => dispatch(setActiveView(OfficeWhereaboutsView.CarPark))}
                                   isActive={activeView === OfficeWhereaboutsView.CarPark} />
        </>}

      </ViewControls>
      <OfficesListContainer>

        {activeView === OfficeWhereaboutsView.Office && <>
          {officeConfigurationType === OfficeConfigurationType.Office && <SingleOfficeUsageView office={rootOffice} isVisible={true} />}
          {officeConfigurationType === OfficeConfigurationType.AreasAndDesks && <AreaAndDeskUsageView />}
        </>}
        {activeView === OfficeWhereaboutsView.CarPark && <OfficeCarParkingList />}
      </OfficesListContainer>
    </OfficesUsageListViewContainer>
  )
}

const OfficesUsageListViewContainer = styled.div`
  padding: 8px 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  justify-content: space-between;
  @media (${tablet}) {
    padding: 24px 48px 100px 24px;
    flex-direction: row;
  }
`

const ViewControls = styled.div`
  padding: 8px 0;
  display: flex;
  width: 100%;
  margin-right: 24px;
  margin-bottom: 16px;
  & > * {
    flex: 1;
  }
  @media (${tablet}) {
    padding: 8px 16px;
    width: 180px;
    flex-direction: column;
    justify-content: flex-start;
    margin-bottom: 0;
    & > * {
      flex: unset;
      margin-bottom: 16px;
      width: 100%;
    }
  }
`

const OfficesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`
